import * as buttons from 'smg/buttons';
import { GetChildren, GetLineItems, GetSizes, GetTypes } from 'smg/customer-api/lineitems';
import * as models from 'smg/customer-api/models';
import * as quoterequests from 'smg/customer-api/quoterequests';
import { Session } from 'smg/customer-api/session';
import { ShowError } from 'smg/errors';
import { BlockPastDates, ReportValidity } from 'smg/form';
import { CleanNumber } from 'smg/number';
import { Phone } from 'smg/phone';
import * as template from 'smg/template';
import { Uploader } from 'smg/uploads';
import { Relref } from '../hugo';

declare interface myWindow extends Window {
    readonly quoteReceived: string;
    dataLayer: Record<string, unknown>[];
}

declare const window: myWindow;

interface extra {
    name: string;
    stepContainer: HTMLElement;
    labels: HTMLLabelElement[];
    currentSelection?: HTMLInputElement;
}

void main();

async function main() {
    const $form = document.querySelector('#form') as HTMLFormElement;
    const $fieldset = $form.querySelector('fieldset') as HTMLFieldSetElement;
    const $infoContainer = $form.querySelector('#info') as HTMLElement;
    const $lanyardInfo = $form.querySelector('#lanyard-info') as HTMLElement;
    const $quantity = $infoContainer.querySelector('#quantity') as HTMLInputElement;
    const $projectName = $infoContainer.querySelector('#project-name') as HTMLInputElement;
    const $budget = $infoContainer.querySelector('#budget') as HTMLInputElement;
    const $deadlineRadios = Array.from($infoContainer.querySelectorAll('input[name="deadline-radio"]')) as HTMLInputElement[];
    const $dateNeededContainer = $infoContainer.querySelector('#date-needed-container') as HTMLElement;
    const $dateNeeded = $dateNeededContainer.querySelector('#date-needed') as HTMLInputElement;
    const revalidateDateNeeded = BlockPastDates($dateNeeded, { earliest: 'tomorrow' });
    const $uploads = $infoContainer.querySelector('#uploads') as HTMLLabelElement;
    const $thumbContainer = $infoContainer.querySelector('#uploads-thumbnails') as HTMLElement;
    const $projectNotes = $infoContainer.querySelector('#project-notes') as HTMLTextAreaElement;
    const $firstName = $infoContainer.querySelector('#first-name') as HTMLInputElement;
    const $lastName = $infoContainer.querySelector('#last-name') as HTMLInputElement;
    const $email = $infoContainer.querySelector('#email') as HTMLInputElement;
    const $phone = $infoContainer.querySelector('#phone') as HTMLInputElement;
    const $submitBtn = $infoContainer.querySelector('#submit-btn') as HTMLButtonElement;

    const u = new Uploader($uploads, {
        thumbnails: $thumbContainer,
        confirmBtn: $submitBtn,
    });

    const p = new Phone($phone);

    let currProduct: string;
    let currProductType: string;
    let currSize: string;

    let currStep: HTMLElement;

    let lanyardExtras: models.QuoteRequestCreateExtra[] | undefined;

    let headerHeight = (document.querySelector('.site-header') as HTMLHeadElement).offsetHeight + 10;
    $infoContainer.setAttribute('style', `scroll-margin-top: ${headerHeight}px`);
    window.addEventListener('resize', () => {
        if (currStep) {
            headerHeight = (document.querySelector('.site-header') as HTMLHeadElement).offsetHeight + 10;
            currStep.setAttribute('style', `scroll-margin-top: ${headerHeight}px`);
            $infoContainer.setAttribute('style', `scroll-margin-top: ${headerHeight}px`);
        }
    });

    for (const $radio of $deadlineRadios) {
        $radio.addEventListener('change', () => {
            const $deadline = $infoContainer.querySelector('input[name="deadline-radio"]:checked') as HTMLInputElement;
            if ($deadline.value === 'has-deadline') {
                $dateNeededContainer.classList.remove('d-none');
                $dateNeeded.required = true;
            } else {
                $dateNeededContainer.classList.add('d-none');
                $dateNeeded.required = false;
                // Hidden, not disabled — drop the value so a date typed before
                // switching to "no deadline" neither blocks the submit with an
                // invisible error nor rides along on the quote request.
                $dateNeeded.value = '';
                revalidateDateNeeded();
            }
        });
    }

    const $stepSkeleton = document.querySelector('#step-skeleton');

    await GetLineItems().then(resp => {
        // Drop the skeleton and build the real step in the same task, so the browser never
        // paints the gap between them and the swap costs no layout shift.
        $stepSkeleton?.remove();

        const title = 'What kind of product are you looking for?<span class="text-danger">*</span>';
        const $stepOne = createStep(title, ['product-step']);

        const $labelEls: HTMLLabelElement[] = [];

        for (const product of resp) {
            if (product.networkLineItem?.enabled) {
                $labelEls.push(createInput($stepOne, 'product-select', product.networkNamePlural(), `products/product-${imgNameFormat(product.networkNamePlural())}`));
            }
        }

        $stepOne.scrollIntoView({
            behavior: 'smooth',
        });

        for (const $label of $labelEls) {
            $label.addEventListener('click', async e => {
                e.preventDefault();

                let extrasSteps: extra[] = [];
                const extras: models.ChildLineItem[] = [];

                checkedAndRequired($label.querySelector('input') as HTMLInputElement);

                currProduct = ($label.querySelector('input') as HTMLInputElement).value;

                addRemoveBorders($label, Array.from(($stepOne as HTMLElement).querySelectorAll('label')));

                const title = `What type of ${($label.querySelector('input') as HTMLInputElement).value} are you looking for?`;
                const $stepTwo = createStep(title, ['type-step', `${currProduct}-type-step`]);

                removeSteps(['product-step']);

                await GetTypes(($label.querySelector('input') as HTMLInputElement).value).then(resp => {
                    const $labelEls: HTMLLabelElement[] = [];

                    for (const type of resp) {
                        if (type.networkLineItemType?.enabled) {
                            $labelEls.push(createInput(
                                $stepTwo,
                                'product-type-select',
                                type.networkNamePlural(),
                                `product-types/${currProduct.toLowerCase().replaceAll(' ', '-')}/type-${imgNameFormat(type.networkNamePlural())}`,
                            ));
                        }
                    }

                    if (e.target instanceof HTMLElement && !previouslySelected(e.target)) {
                        $stepTwo.scrollIntoView({
                            behavior: 'smooth',
                        });
                    }

                    for (const $label of $labelEls) {
                        $label.addEventListener('click', async e => {
                            e.preventDefault();

                            checkedAndRequired($label.querySelector('input') as HTMLInputElement);

                            currProductType = ($label.querySelector('input') as HTMLInputElement).value;

                            addRemoveBorders($label, Array.from(($stepTwo as HTMLElement).querySelectorAll('label')));
                            removeSteps(['product-step', 'type-step']);

                            const title = `What size ${currProduct} are you looking for?`;
                            const $stepThree = createStep(title, ['size-step', `${currProduct}-size-step`]);

                            await GetSizes(currProduct, currProductType).then(resp => {
                                const $labelEls: HTMLLabelElement[] = [];

                                for (const size of resp) {
                                    if (size.networkLineItemTypeSize?.enabled) {
                                        const sizeName = size.networkName();
                                        if (currProduct === 'Challenge Coins') {
                                            $labelEls.push(createInput(
                                                $stepThree,
                                                'size-select',
                                                sizeName,
                                                `child-items/${currProduct.toLowerCase().replaceAll(' ', '-')}/size/${imgNameFormat(sizeName.replace(/"/g, ''))}`,
                                            ));
                                        } else {
                                            $labelEls.push(createInput($stepThree, 'size-select', sizeName));
                                        }
                                    }
                                }

                                if (e.target instanceof HTMLElement && !previouslySelected(e.target)) {
                                    $stepThree.scrollIntoView({
                                        behavior: 'smooth',
                                    });
                                }

                                for (const $label of $labelEls) {
                                    for (const $name of Array.from($label.querySelectorAll('h3'))) {
                                        $name.classList.add('fs-3', 'my-10');
                                    }

                                    $label.addEventListener('click', async e => {
                                        e.preventDefault();

                                        checkedAndRequired($label.querySelector('input') as HTMLInputElement);

                                        currSize = ($label.querySelector('input') as HTMLInputElement).value;

                                        addRemoveBorders($label, Array.from(($stepThree as HTMLElement).querySelectorAll('label')));
                                        removeSteps(['product-step', 'type-step', 'size-step']);

                                        await GetChildren(currProduct).then(resp => {
                                            for (const childLineItem of resp) {
                                                if (childLineItem.networkChildLineItem?.quoteForm) {
                                                    extras.push(childLineItem);
                                                }
                                            }
                                            extrasSteps = [];
                                            const extrasSortOrder = ['Plating', 'Edge', 'Backing', 'Border', 'Attachments', 'Presentation'];
                                            extras.sort((a, b) => extrasSortOrder.indexOf(a.networkName()) - extrasSortOrder.indexOf(b.networkName()));
                                            const extrasSet = new Set(extras);
                                            extrasSet.forEach(childLineItem => {
                                                const childName = childLineItem.networkName();
                                                const title = `What kind of ${childName} would you like?`;
                                                const $step = createStep(title, [`${childName.toLowerCase()}-step`, `${currProduct}-${childName.toLowerCase()}-step`], true);

                                                const $labelEls: HTMLLabelElement[] = [];

                                                for (const option of childLineItem.childLineItemOptions) {
                                                    if (option.networkChildLineItemOption?.enabled) {
                                                        $labelEls.push(createInput(
                                                            $step,
                                                            `${childName}-select`,
                                                            option.networkName(),
                                                            `child-items/${currProduct.toLowerCase().replaceAll(' ', '-')}/${childName.toLowerCase()}/${imgNameFormat(option.networkName())}`,
                                                        ));
                                                    }
                                                }
                                                if (childName === 'Border') {
                                                    $labelEls.push(createInput(
                                                        $step,
                                                        'border-select',
                                                        'None',
                                                        `child-items/${currProduct.toLowerCase().replaceAll(' ', '-')}/${childName.toLowerCase()}/${imgNameFormat('none')}`,
                                                    ));
                                                }

                                                const extra: extra = {
                                                    name: childName,
                                                    stepContainer: $step,
                                                    labels: $labelEls,
                                                };

                                                extrasSteps.push(extra);
                                            });
                                        });

                                        extrasSteps[0].stepContainer.classList.remove('d-none');
                                        if (e.target instanceof HTMLElement && !previouslySelected(e.target)) {
                                            extrasSteps[0].stepContainer.scrollIntoView({
                                                behavior: 'smooth',
                                            });
                                        }

                                        for (let i = 0; i < extrasSteps.length; i++) {
                                            for (const $label of extrasSteps[i].labels) {
                                                $label.addEventListener('click', e => {
                                                    e.preventDefault();

                                                    checkedAndRequired($label.querySelector('input') as HTMLInputElement);

                                                    extrasSteps[i].currentSelection = $label.querySelector('input') as HTMLInputElement;

                                                    addRemoveBorders($label, Array.from((extrasSteps[i].stepContainer as HTMLElement).querySelectorAll('label')));

                                                    if (extrasSteps[i + 1]) {
                                                        extrasSteps[i + 1].stepContainer.classList.remove('d-none');

                                                        if (e.target instanceof HTMLElement && !previouslySelected(e.target)) {
                                                            extrasSteps[i + 1].stepContainer.scrollIntoView({
                                                                behavior: 'smooth',
                                                            });
                                                        }
                                                    } else {
                                                        const currExtras: models.QuoteRequestCreateChild[] = [];
                                                        for (const extra of extrasSteps) {
                                                            currExtras.push(new models.QuoteRequestCreateChild({
                                                                child: extra.name,
                                                                option: extra.currentSelection?.value,
                                                            }));
                                                        }

                                                        if (currProduct === 'Lanyards') {
                                                            $lanyardInfo.classList.remove('d-none');
                                                        } else {
                                                            $lanyardInfo.classList.add('d-none');
                                                        }
                                                        $fieldset.appendChild($infoContainer);
                                                        $infoContainer.classList.remove('d-none');

                                                        $infoContainer.scrollIntoView({
                                                            behavior: 'smooth',
                                                        });

                                                        $form.addEventListener('submit', async e => {
                                                            e.preventDefault();
                                                            if (!ReportValidity($form)) {
                                                                return false;
                                                            }

                                                            $fieldset.setAttribute('disabled', 'true');
                                                            buttons.Start($submitBtn);

                                                            if (currProduct === 'Lanyards') {
                                                                const $lanyardText = $lanyardInfo.querySelector('#lanyard-text') as HTMLInputElement;
                                                                const $lanyardTextColor = $lanyardInfo.querySelector('#lanyard-text-color') as HTMLInputElement;
                                                                const $lanyardColor = $lanyardInfo.querySelector('#lanyard-color') as HTMLInputElement;

                                                                lanyardExtras = [new models.QuoteRequestCreateExtra({
                                                                    name: 'Custom Lanyard Text',
                                                                    value: $lanyardText.value,
                                                                }),
                                                                new models.QuoteRequestCreateExtra({
                                                                    name: 'Lanyard Text Color',
                                                                    value: $lanyardTextColor.value,
                                                                }),
                                                                new models.QuoteRequestCreateExtra({
                                                                    name: 'Lanyard Color',
                                                                    value: $lanyardColor.value,
                                                                })];
                                                            } else {
                                                                lanyardExtras = undefined;
                                                            }

                                                            await quoterequests.NewQuoteRequest({
                                                                form: 'free quote',
                                                                name: `${$firstName.value} ${$lastName.value}`,
                                                                company: '',
                                                                phone: await p.format(),
                                                                email: $email.value,
                                                                lineItem: currProduct,
                                                                type: currProductType,
                                                                size: currSize,
                                                                children: currExtras,
                                                                quantity: parseInt(CleanNumber($quantity.value), 10) || 0,
                                                                budget: parseFloat(CleanNumber($budget.value)) || 0,
                                                                project: $projectName.value,
                                                                dateNeeded: $dateNeeded.value,
                                                                notes: $projectNotes.value,
                                                                uploadIDs: await u.getUploadIDs(),
                                                                extras: lanyardExtras,
                                                            }).then(async resp => {
                                                                const sess = await Session().catch(() => undefined);
                                                                const dataLayer = window.dataLayer || [];
                                                                dataLayer.push({
                                                                    'event': 'depot_hugo_freequote_submission',
                                                                    'userData': {
                                                                        'email': sess?.user.emailAddress.email || $email.value,
                                                                        'phone': sess?.user.phoneNumber.phone || await p.format(),
                                                                        'firstName': sess?.user.firstName || $firstName.value,
                                                                        'lastName': sess?.user.lastName || $lastName.value,
                                                                    },
                                                                    'transactionId': resp.id,
                                                                    'total': resp.quotes?.[0].cachedTotal,
                                                                });

                                                                window.location.href = `${Relref('/free-quote-success')}?id=${resp?.quotes?.[0].key || ''}`;
                                                            }).catch(err => {
                                                                ShowError(err);
                                                                $fieldset.removeAttribute('disabled');
                                                                buttons.Stop($submitBtn);
                                                            });
                                                        });
                                                    }
                                                });
                                            }
                                        }
                                    });
                                }
                            });
                        });
                    }
                });
            });
        }
    }).catch(err => {
        // Leaving the skeleton up would shimmer forever on a failed load.
        $stepSkeleton?.remove();
        ShowError(err);
    });

    function createStep(groupTitle: string, classNames: string[], extra?: boolean): HTMLElement {
        const $inputGroup = template.Clone('.input-group-template').firstElementChild as HTMLElement;
        if (extra) {
            $inputGroup.classList.add('d-none');
        }
        for (const className of classNames) {
            $inputGroup.classList.add(imgNameFormat(className));
        }
        const $inputGroupTitle = $inputGroup?.querySelector('h2') as HTMLElement;
        $inputGroupTitle.innerHTML = groupTitle;

        $inputGroup.setAttribute('style', `scroll-margin-top: ${headerHeight}px`);
        currStep = $inputGroup;
        return $inputGroup;
    }

    function removeSteps(classNames: string[]) {
        const $steps = Array.from($fieldset.querySelectorAll('.step'));
        for (const $step of $steps) {
            const classList = Array.from($step.classList);

            if (!classList.some(className => classNames.includes(className))) {
                $step.remove();
            }
        }
        $infoContainer.classList.add('d-none');
    }

    function createInput($step: HTMLElement, inputName: string, itemName: string, path?: string) {
        const $inputTemplate = template.Clone('.input-template');
        const $label = $inputTemplate.firstElementChild as HTMLLabelElement;

        ($label.querySelector('h3') as HTMLElement).textContent = itemName;
        ($label.querySelector('input') as HTMLInputElement).name = inputName;
        ($label.querySelector('input') as HTMLInputElement).value = itemName;

        if (path) {
            const $template = document.querySelector<HTMLTemplateElement>(`.quote-image-template[data-file^="/img/free-quote/${path}"]`);
            if ($template) {
                const $imgTemplate = template.Clone($template);
                $label.insertBefore($imgTemplate, $label.firstElementChild);
            }
        }

        $step?.querySelector('.input-container')?.appendChild($inputTemplate);
        if ($step) {
            $fieldset.appendChild($step);
        }

        return $label;
    }

    function checkedAndRequired($inputEl: HTMLInputElement) {
        $inputEl.checked = true;
        $inputEl.required = true;
    }

    function previouslySelected($tar: HTMLElement): boolean {
        if ($tar.closest('.input-container')?.classList.contains('previously-selected')) {
            return true;
        } else {
            $tar.closest('.input-container')?.classList.add('previously-selected');
            return false;
        }
    }

    function addRemoveBorders($tar: HTMLElement, $allInputs: HTMLElement[]) {
        for (const $ of $allInputs) {
            $.classList.remove('border', 'border-primary', 'rounded');
            $tar.classList.add('border', 'border-primary', 'rounded');
        }
    }

    function imgNameFormat(apiStr: string): string {
        return apiStr.replace(/\s+/g, '-').replace(/%/g, '-percent').toLowerCase();
    }
}